import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SonioxClient } from "@soniox/speech-to-text-web";
import { supabase } from "@/integrations/supabase/client";
import { sleep } from "@/lib/utils/async-retry";
import {
  CONNECTION_PROBE_INTERVAL_MS,
  FINALIZE_WATCHDOG_MS,
  SONIOX_CONNECT_TIMEOUT_MS,
  SONIOX_TERMS,
  buildSonioxContext,
} from "./constants";
import { evaluateConnection } from "./connection-health";
import { createSonioxStartupSignal, isActiveSonioxClient } from "./soniox-client-lifecycle";
import { isFatalSonioxError } from "./soniox-error-classification";
import type { RecordingPhase, TranscriptionToken } from "./types";

const SONIOX_BOOTSTRAP_MAX_ATTEMPTS = 2;
const SONIOX_BOOTSTRAP_RETRY_DELAY_MS = 1000;

interface UseSonioxRecordingParams {
  petId: string;
  ensureSession: () => Promise<string | null>;
  reset: () => void;
  metadata: Record<string, unknown>;
  sessionIdRef: React.RefObject<string | null>;
  /** Disparado quando a captura termina (encerrada pelo usuario ou pelo watchdog). */
  onFinalized: (finalTranscript: string) => void;
  /** Disparado quando o WebSocket cai em plena gravacao. O transcript e preservado. */
  onConnectionLost?: (message: string) => void;
}

export const useSonioxRecording = ({
  petId,
  ensureSession,
  reset,
  metadata,
  sessionIdRef,
  onFinalized,
  onConnectionLost,
}: UseSonioxRecordingParams) => {
  const { t } = useTranslation("medicalRecords");
  const [recordingPhase, setRecordingPhase] = useState<RecordingPhase>("idle");
  const [isStarting, setIsStarting] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [isInterrupted, setIsInterrupted] = useState(false);
  const [sonioxError, setSonioxError] = useState<string | null>(null);
  const [editableTranscript, setEditableTranscript] = useState("");
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [finalTokens, setFinalTokens] = useState<TranscriptionToken[]>([]);
  const [nonFinalTokens, setNonFinalTokens] = useState<TranscriptionToken[]>([]);

  const sonioxClientRef = useRef<SonioxClient | null>(null);
  const latestTranscriptRef = useRef("");
  const baseTranscriptRef = useRef("");
  const finalizeReasonRef = useRef<"stop" | "pause" | null>(null);
  const sonioxApiKeyRef = useRef<string | null>(null);
  const durationIntervalRef = useRef<number | null>(null);
  const finalTokensRef = useRef<TranscriptionToken[]>([]);
  const nonFinalTokensRef = useRef<TranscriptionToken[]>([]);
  const startupSignalRef = useRef<ReturnType<typeof createSonioxStartupSignal> | null>(null);
  const finalizeWatchdogRef = useRef<number | null>(null);
  const bufferSamplesRef = useRef<number[]>([]);

  const clearFinalizeWatchdog = useCallback(() => {
    if (finalizeWatchdogRef.current) {
      window.clearTimeout(finalizeWatchdogRef.current);
      finalizeWatchdogRef.current = null;
    }
  }, []);

  /**
   * Trata a perda de conexao, venha ela do `onError` do SDK ou das sondas ativas.
   * Preserva o transcript e vai para "paused" (nao "idle") para que a retomada
   * reaproveite `editableTranscript` como nova base.
   */
  const handleConnectionLost = useCallback(
    (message: string) => {
      const client = sonioxClientRef.current;
      if (client) {
        sonioxClientRef.current = null;
        client.cancel();
      }

      bufferSamplesRef.current = [];
      setRecordingPhase("paused");
      setRecordingDuration(0);
      setFinalTokens([]);
      setNonFinalTokens([]);
      finalTokensRef.current = [];
      nonFinalTokensRef.current = [];
      finalizeReasonRef.current = null;
      setIsInterrupted(true);
      onConnectionLost?.(message);
    },
    [onConnectionLost],
  );

  const resetRecordingState = useCallback(
    (clearTranscript = true) => {
      setRecordingPhase("idle");
      setRecordingDuration(0);
      setFinalTokens([]);
      setNonFinalTokens([]);
      finalTokensRef.current = [];
      nonFinalTokensRef.current = [];
      finalizeReasonRef.current = null;

      if (clearTranscript) {
        latestTranscriptRef.current = "";
        baseTranscriptRef.current = "";
        setEditableTranscript("");
        reset();
      }
    },
    [reset],
  );

  const fetchSonioxApiKey = useCallback(async (): Promise<string> => {
    if (sonioxApiKeyRef.current) return sonioxApiKeyRef.current;

    const { data, error: functionError } = await supabase.functions.invoke<{ apiKey: string }>("soniox-api-key", {
      body: {},
    });

    if (functionError) {
      throw new Error(t("transcription.errors.sonioxKeyFetchFailed"));
    }

    const apiKey = data?.apiKey;
    if (!apiKey) {
      throw new Error(t("transcription.errors.sonioxKeyUnavailable"));
    }

    sonioxApiKeyRef.current = apiKey;
    return apiKey;
  }, [t]);

  const cleanupSoniox = useCallback(() => {
    clearFinalizeWatchdog();
    finalizeReasonRef.current = null;
    startupSignalRef.current?.markFailed(new Error("Soniox startup canceled"));
    startupSignalRef.current = null;
    const client = sonioxClientRef.current;
    if (client) {
      sonioxClientRef.current = null;
      client.cancel();
    }
  }, [clearFinalizeWatchdog]);

  const cancelActiveRecording = useCallback(() => {
    clearFinalizeWatchdog();
    finalizeReasonRef.current = null;
    setIsFinalizing(false);
    setIsInterrupted(false);
    startupSignalRef.current?.markFailed(new Error("Soniox startup canceled"));
    startupSignalRef.current = null;
    const client = sonioxClientRef.current;
    if (client) {
      sonioxClientRef.current = null;
      client.cancel();
    }
    setRecordingPhase("idle");
    setRecordingDuration(0);
    latestTranscriptRef.current = baseTranscriptRef.current;
    setEditableTranscript(baseTranscriptRef.current);
  }, [clearFinalizeWatchdog]);

  const handleStartRecording = useCallback(
    async (stream: MediaStream, options?: { preserveTranscript?: boolean }) => {
      if (recordingPhase === "recording" || isStarting) return false;

      try {
        if (!petId) {
          setSonioxError(t("transcription.errors.petRequired"));
          return false;
        }

        const currentSession = await ensureSession();
        if (!currentSession) return false;

        setIsStarting(true);
        setSonioxError(null);
        finalizeReasonRef.current = null;

        // `preserveTranscript` cobre a retomada apos uma reconexao falha, que deixa a
        // fase em "idle" mesmo havendo texto capturado. Sem isso, retomar apagaria a consulta.
        if (recordingPhase === "idle" && !options?.preserveTranscript) {
          reset();
          setEditableTranscript("");
          baseTranscriptRef.current = "";
          latestTranscriptRef.current = "";
          setRecordingDuration(0);
          setFinalTokens([]);
          setNonFinalTokens([]);
          finalTokensRef.current = [];
          nonFinalTokensRef.current = [];
        } else {
          // Cobre tanto a pausa manual quanto a retomada apos queda de conexao:
          // nos dois casos o texto ja capturado (editableTranscript) vira a nova base.
          baseTranscriptRef.current = editableTranscript.trim();
          latestTranscriptRef.current = baseTranscriptRef.current;
          setFinalTokens([]);
          setNonFinalTokens([]);
          finalTokensRef.current = [];
          nonFinalTokensRef.current = [];
        }

        for (let attempt = 1; attempt <= SONIOX_BOOTSTRAP_MAX_ATTEMPTS; attempt++) {
          try {
            if (attempt > 1) {
              cleanupSoniox();
              setRecordingPhase("idle");
              finalizeReasonRef.current = null;
              console.log("[transcription] soniox:bootstrapRetry", {
                attempt,
                ts: new Date().toISOString(),
                tsMs: Date.now(),
              });
              await sleep(SONIOX_BOOTSTRAP_RETRY_DELAY_MS);
            }

            const startup = createSonioxStartupSignal();
            startupSignalRef.current = startup;
            let hasStarted = false;
            const client = new SonioxClient({
              apiKey: fetchSonioxApiKey,
              onStarted: () => {
                if (!isActiveSonioxClient(sonioxClientRef.current, client)) return;
                hasStarted = true;
                startupSignalRef.current = null;
                startup.markStarted();
              },
              onPartialResult: (result) => {
                if (!isActiveSonioxClient(sonioxClientRef.current, client)) return;

                const newFinalTokens: TranscriptionToken[] = [];
                const newNonFinalTokens: TranscriptionToken[] = [];

                for (const token of result.tokens) {
                  if (token.is_final) {
                    newFinalTokens.push(token);
                  } else {
                    newNonFinalTokens.push(token);
                  }
                }

                finalTokensRef.current = [...finalTokensRef.current, ...newFinalTokens].filter(
                  (token) => token.text.trim() !== "<end>",
                );
                nonFinalTokensRef.current = newNonFinalTokens;

                setFinalTokens(finalTokensRef.current);
                setNonFinalTokens(nonFinalTokensRef.current);

                const allFinalText = finalTokensRef.current.map((t) => t.text).join("");
                const nonFinalText = nonFinalTokensRef.current.map((t) => t.text).join("");
                const fullTranscript = baseTranscriptRef.current + allFinalText + nonFinalText;

                latestTranscriptRef.current = fullTranscript;
                setEditableTranscript(fullTranscript);
              },
              onError: (status, message) => {
                if (!isActiveSonioxClient(sonioxClientRef.current, client)) {
                  console.log("[transcription] soniox:onError:ignoredStaleClient", {
                    transcriptionId: sessionIdRef.current ?? null,
                    ts: new Date().toISOString(),
                    tsMs: Date.now(),
                    status,
                    message,
                  });
                  return;
                }

                const resolvedMessage = message ?? t("transcription.errors.audioProcessingError");
                if (!hasStarted) {
                  startupSignalRef.current = null;
                  startup.markFailed(new Error(resolvedMessage));
                  return;
                }

                console.error("[transcription] soniox:onError", {
                  transcriptionId: sessionIdRef.current ?? null,
                  ts: new Date().toISOString(),
                  tsMs: Date.now(),
                  recordingPhase,
                  status,
                  message: resolvedMessage,
                  finalizeReason: finalizeReasonRef.current,
                });

                if (isFatalSonioxError(status, resolvedMessage)) {
                  // Erro permanente (ex.: chave de API invalida): a Soniox chama onStarted
                  // antes de validar a chave, entao isso chega como "erro em plena gravacao"
                  // mesmo sem nunca ter transcrito nada. Reconectar sozinho so repetiria o
                  // mesmo erro para sempre — trata como terminal, sem loop automatico.
                  sonioxClientRef.current = null;
                  client.cancel();
                  resetRecordingState(false);
                  setSonioxError(resolvedMessage);
                  return;
                }

                // NUNCA limpar o transcript aqui: este callback dispara em queda de rede,
                // e o texto ja capturado precisa sobreviver.
                handleConnectionLost(resolvedMessage);
              },
              onFinished: () => {
                if (!isActiveSonioxClient(sonioxClientRef.current, client)) return;

                const finalTranscript = latestTranscriptRef.current.trim();
                console.log("[transcription] soniox:onFinished", {
                  transcriptionId: sessionIdRef.current ?? null,
                  ts: new Date().toISOString(),
                  tsMs: Date.now(),
                  finalizeReason: finalizeReasonRef.current,
                  finalTranscriptLength: finalTranscript.length,
                });
                if (finalTranscript) {
                  setEditableTranscript(finalTranscript);
                  baseTranscriptRef.current = finalTranscript;
                }

                if (finalizeReasonRef.current === "stop") {
                  clearFinalizeWatchdog();
                  resetRecordingState(false);

                  if (finalTranscript) {
                    console.log("[transcription] soniox:onFinished:finalize", {
                      transcriptionId: sessionIdRef.current ?? null,
                      ts: new Date().toISOString(),
                      tsMs: Date.now(),
                      finalTranscriptLength: finalTranscript.length,
                    });
                    onFinalized(finalTranscript);
                  } else {
                    console.warn("[transcription] soniox:onFinished:emptyTranscript", {
                      transcriptionId: sessionIdRef.current ?? null,
                      ts: new Date().toISOString(),
                      tsMs: Date.now(),
                      finalizeReason: finalizeReasonRef.current,
                    });
                    setSonioxError(t("transcription.errors.emptyTranscript"));
                    setIsFinalizing(false);
                  }
                } else if (finalizeReasonRef.current === "pause") {
                  setRecordingPhase("paused");
                } else {
                  setRecordingPhase("idle");
                  setFinalTokens([]);
                  setNonFinalTokens([]);
                  finalTokensRef.current = [];
                  nonFinalTokensRef.current = [];
                }

                finalizeReasonRef.current = null;
                sonioxClientRef.current = null;
              },
            });

            sonioxClientRef.current = client;

            const dynamicContext = buildSonioxContext(metadata);

            // Sem um teto explicito, um handshake que trava (comum logo apos a rede
            // voltar, antes de estabilizar de verdade) fica pendurado para sempre:
            // isStarting nunca volta a false e o loop de reconexao nunca tenta de novo.
            let connectTimeoutId: number | null = null;
            const connectTimeout = new Promise<never>((_, reject) => {
              connectTimeoutId = window.setTimeout(() => {
                reject(new Error(t("transcription.errors.connectTimeout")));
              }, SONIOX_CONNECT_TIMEOUT_MS);
            });

            try {
              await Promise.race([
                (async () => {
                  await client.start({
                    model: "stt-rt-v4",
                    languageHints: ["pt"],
                    enableEndpointDetection: true,
                    stream,
                    context: {
                      text: dynamicContext,
                      terms: SONIOX_TERMS,
                    },
                  });
                  await startup.promise;
                })(),
                connectTimeout,
              ]);
            } finally {
              if (connectTimeoutId) window.clearTimeout(connectTimeoutId);
            }

            if (!isActiveSonioxClient(sonioxClientRef.current, client)) return false;

            setRecordingPhase("recording");
            setIsInterrupted(false);
            return true;
          } catch (attemptError) {
            const shouldRetry = Boolean(sonioxClientRef.current);
            cleanupSoniox();
            setRecordingPhase("idle");
            finalizeReasonRef.current = null;
            if (!shouldRetry) return false;
            if (attempt === SONIOX_BOOTSTRAP_MAX_ATTEMPTS) {
              console.error("Não foi possível iniciar a transcrição:", attemptError);
              setSonioxError(
                attemptError instanceof Error
                  ? attemptError.message
                  : t("transcription.errors.startFailedGeneric"),
              );
              return false;
            }
          }
        }

        return false;
      } catch (startError) {
        console.error("Não foi possível iniciar a transcrição:", startError);
        setSonioxError(
          startError instanceof Error ? startError.message : t("transcription.errors.startFailedGeneric"),
        );
        cleanupSoniox();
        setRecordingPhase("idle");
        finalizeReasonRef.current = null;
        return false;
      } finally {
        setIsStarting(false);
      }
    },
    [
      recordingPhase,
      isStarting,
      petId,
      ensureSession,
      reset,
      editableTranscript,
      fetchSonioxApiKey,
      metadata,
      sessionIdRef,
      cleanupSoniox,
      handleConnectionLost,
      onFinalized,
      clearFinalizeWatchdog,
      resetRecordingState,
      t,
    ],
  );

  const handleStopRecording = useCallback(() => {
    const stopLogPayload = {
      transcriptionId: sessionIdRef.current ?? null,
      ts: new Date().toISOString(),
      tsMs: Date.now(),
      recordingPhase,
      transcriptLength: (latestTranscriptRef.current.trim() || editableTranscript.trim()).length,
      hasActiveClient: Boolean(sonioxClientRef.current),
    };
    console.log("[transcription] stopRecording:requested", stopLogPayload);

    // Entra em "finalizing" de forma sincrona, no clique: o cronometro depende
    // disso e nao pode esperar o onFinished do fornecedor, que pode nunca chegar.
    setIsFinalizing(true);
    setRecordingDuration(0);
    // Encerrar durante uma interrupcao sai do estado interrompido: sem isso o
    // ciclo de reconexao continuaria e a maquina de estados voltaria a "interrupted".
    setIsInterrupted(false);
    finalizeReasonRef.current = "stop";
    const client = sonioxClientRef.current;

    if (client) {
      console.log("[transcription] stopRecording:clientStop", {
        ...stopLogPayload,
        finalizeReason: "stop",
      });
      client.stop();

      finalizeWatchdogRef.current = window.setTimeout(() => {
        // O onFinished do Soniox nao chegou. Finaliza com o que ja esta em memoria:
        // o callback do fornecedor nunca pode ser caminho critico.
        console.warn("[transcription] finalize:watchdogFired", {
          transcriptionId: sessionIdRef.current ?? null,
          tsMs: Date.now(),
        });
        const finalTranscript = latestTranscriptRef.current.trim() || editableTranscript.trim();
        sonioxClientRef.current = null;
        client.cancel();
        resetRecordingState(false);
        onFinalized(finalTranscript);
      }, FINALIZE_WATCHDOG_MS);
    } else {
      const finalTranscript = latestTranscriptRef.current.trim() || editableTranscript.trim();
      resetRecordingState(false);

      if (finalTranscript) {
        console.log("[transcription] stopRecording:finalizeWithoutClient", {
          ...stopLogPayload,
          finalTranscriptLength: finalTranscript.length,
        });
        onFinalized(finalTranscript);
      } else {
        setSonioxError(t("transcription.errors.emptyTranscript"));
        setIsFinalizing(false);
      }
    }
  }, [editableTranscript, onFinalized, recordingPhase, resetRecordingState, sessionIdRef, t]);

  const handlePauseRecording = useCallback(() => {
    const client = sonioxClientRef.current;
    if (!client) return;
    finalizeReasonRef.current = "pause";
    client.stop();
  }, []);

  const handleResumeRecording = useCallback(
    async (stream: MediaStream) => {
      // Retomar NUNCA pode zerar o que ja foi capturado, mesmo que a fase tenha
      // caido para "idle" por causa de uma tentativa de reconexao que falhou.
      return handleStartRecording(stream, { preserveTranscript: true });
    },
    [handleStartRecording],
  );

  useEffect(() => {
    if (recordingPhase === "recording" && !isFinalizing) {
      if (durationIntervalRef.current) {
        window.clearInterval(durationIntervalRef.current);
      }
      durationIntervalRef.current = window.setInterval(() => {
        setRecordingDuration((previous) => previous + 1);
      }, 1000);
    } else if (durationIntervalRef.current) {
      window.clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    return () => {
      if (durationIntervalRef.current) {
        window.clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    };
  }, [isFinalizing, recordingPhase]);

  useEffect(() => clearFinalizeWatchdog, [clearFinalizeWatchdog]);

  /**
   * Sondagem ativa da conexao durante a captura.
   *
   * Necessaria porque o `onError` do SDK nao e confiavel como sinal de queda: ao
   * desligar o Wi-Fi o WebSocket segue OPEN por dezenas de segundos ate o TCP
   * expirar, e nesse intervalo o cronometro continuaria correndo enquanto o audio
   * some. O evento `offline` pega a queda na hora; o buffer crescente pega o caso
   * "conectado mas sem internet", que `navigator.onLine` nao enxerga.
   */
  useEffect(() => {
    if (recordingPhase !== "recording" || isFinalizing) {
      bufferSamplesRef.current = [];
      return;
    }

    const reportLost = (reason: string) => {
      console.warn("[transcription] connection:lostDetected", {
        transcriptionId: sessionIdRef.current ?? null,
        tsMs: Date.now(),
        reason,
      });
      handleConnectionLost(t("transcription.connection.lostTitle"));
    };

    const handleOffline = () => reportLost("navigator_offline");
    window.addEventListener("offline", handleOffline);

    const intervalId = window.setInterval(() => {
      const socket = (sonioxClientRef.current as unknown as { _websocket?: WebSocket | null })?._websocket;
      const bufferedAmount = socket?.bufferedAmount ?? 0;
      bufferSamplesRef.current = [...bufferSamplesRef.current, bufferedAmount].slice(-8);

      const verdict = evaluateConnection({
        isOnline: navigator.onLine,
        socketReadyState: socket?.readyState ?? null,
        bufferedAmountSamples: bufferSamplesRef.current,
      });

      if (verdict !== "healthy") reportLost(verdict);
    }, CONNECTION_PROBE_INTERVAL_MS);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.clearInterval(intervalId);
    };
  }, [handleConnectionLost, isFinalizing, recordingPhase, sessionIdRef, t]);

  return {
    recordingPhase,
    isStarting,
    isFinalizing,
    isInterrupted,
    sonioxError,
    editableTranscript,
    recordingDuration,
    finalTokens,
    nonFinalTokens,
    setEditableTranscript,
    setSonioxError,
    setIsFinalizing,
    setIsInterrupted,
    cleanupSoniox,
    cancelActiveRecording,
    handleStartRecording,
    handleStopRecording,
    handlePauseRecording,
    handleResumeRecording,
  };
};
